import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { JobStatusPill } from '@/components/jobs/JobStatusPill';
import { dashboardApi } from '@/lib/api';
import { fmtMoney, fmtDate } from '@/lib/format';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle, TrendingUp, TrendingDown, Calendar, Bell, Package, Receipt,
  Home as HomeIcon, RefreshCw, CheckCircle2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Navigate } from 'react-router-dom';

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const d = await dashboardApi.get();
      setData(d);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (user?.role === 'cleaner') return <Navigate to="/my-day" replace />;

  const kpi = data?.kpi || {};
  const alerts = data?.alerts || {};

  // "2026-08" -> "Aug" for the chart axis.
  const revenueTrend = (data?.revenue_trend || []).map(point => {
    const [year, month] = point.month.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    return { ...point, label: date.toLocaleString(undefined, { month: 'short' }) };
  });

  // Month-to-date against a full previous month is not a like-for-like comparison,
  // so only show a delta once there is a prior month to compare against.
  const prev = kpi.revenue_prev_month;
  const revenueDelta = prev > 0 ? Math.round(((kpi.revenue_mtd - prev) / prev) * 100) : null;

  return (
    <AppShell title="Dashboard">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Welcome back, {user?.name?.split(' ')[0]}</h1>
            <p className="text-sm text-muted-foreground">Here's what's happening across your cleaning operations today.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Today's jobs" value={kpi.jobs_today ?? 0} icon={Calendar} testId="kpi-jobs-today" loading={loading} />
          <KpiCard label="Upcoming (7d)" value={kpi.jobs_upcoming_7d ?? 0} icon={TrendingUp} testId="kpi-jobs-upcoming" loading={loading} />
          <KpiCard
            label="Revenue MTD"
            value={fmtMoney(kpi.revenue_mtd)}
            icon={Receipt}
            testId="kpi-revenue"
            loading={loading}
            delta={revenueDelta}
            deltaLabel="vs last month"
          />
          <KpiCard label="Alerts" value={kpi.alerts ?? 0} icon={Bell} testId="kpi-alerts" loading={loading} tone={kpi.alerts > 0 ? 'warn' : 'default'} />
        </div>

        <Card>
          <CardHeader className="flex flex-row items-baseline justify-between">
            <CardTitle className="text-base">Revenue</CardTitle>
            <span className="text-xs text-muted-foreground">Last 6 months</span>
          </CardHeader>
          <CardContent className="pt-0">
              {loading ? (
                <Skeleton className="h-[220px] w-full" />
              ) : (
                <div className="h-[220px]" data-testid="dashboard-revenue-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueTrend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                      <defs>
                        <linearGradient id="fc-revenue-fill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        width={64}
                        tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={v => fmtMoney(v)}
                      />
                      <Tooltip
                        cursor={{ stroke: 'hsl(var(--border))' }}
                        formatter={v => [fmtMoney(v), 'Revenue']}
                        contentStyle={{
                          background: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: 'var(--radius)',
                          fontSize: 12,
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="hsl(var(--chart-1))"
                        strokeWidth={2}
                        fill="url(#fc-revenue-fill)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <Card className="xl:col-span-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Jobs needing attention</CardTitle>
              <Link to="/jobs" className="text-xs font-medium text-primary hover:underline">View all</Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3 py-1">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-3 w-1/3" />
                      </div>
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </div>
                  ))}
                </div>
              ) : (!data?.jobs_needing_attention || data.jobs_needing_attention.length === 0) ? (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-[hsl(var(--fc-success))]" />
                  No jobs need attention right now.
                </div>
              ) : (
                <div className="divide-y">
                  {data.jobs_needing_attention.map(j => (
                    <Link key={j.id} to={`/jobs/${j.id}`} className="flex items-center justify-between gap-3 py-3 hover:bg-muted/40 -mx-2 px-2 rounded-md">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {j.customer_name || 'Unknown customer'}
                          {j.property_nickname ? <span className="text-muted-foreground font-normal"> • {j.property_nickname}</span> : null}
                        </div>
                        <div className="text-xs text-muted-foreground">{fmtDate(j.scheduled_at)}</div>
                      </div>
                      <JobStatusPill status={j.status} />
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="xl:col-span-4 space-y-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Alerts</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <AlertRow icon={Package} label="Low inventory items" count={alerts.low_inventory ?? 0} to="/inventory?low=1" />
                <AlertRow icon={AlertTriangle} label="Open issue reports" count={alerts.open_issues ?? 0} to="/issues" />
                <AlertRow icon={Receipt} label="Unpaid invoices" count={alerts.unpaid_invoices ?? 0} to="/invoices" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">iCal sync</CardTitle></CardHeader>
              <CardContent>
                {(!data?.ical_status || data.ical_status.length === 0) ? (
                  <div className="text-sm text-muted-foreground py-2">No properties with iCal configured yet.</div>
                ) : (
                  <div className="space-y-2">
                    {data.ical_status.map(p => (
                      <Link key={p.id} to={`/properties/${p.id}`} className="flex items-center justify-between p-2 rounded-md hover:bg-muted/40">
                        <div className="flex items-center gap-2 text-sm">
                          <HomeIcon className="h-4 w-4 text-muted-foreground" />
                          {p.nickname}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <RefreshCw className="h-3 w-3" />
                          {p.last_ical_sync ? fmtDate(p.last_ical_sync) : 'Never synced'}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function KpiCard({ label, value, icon: Icon, testId, tone, loading, delta, deltaLabel }) {
  const isUp = delta > 0;
  const DeltaIcon = isUp ? TrendingUp : TrendingDown;

  return (
    <Card data-testid={testId} className={tone === 'warn' ? 'border-[hsl(var(--fc-warning)/0.5)]' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
          <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${tone === 'warn' ? 'bg-[hsl(var(--fc-warning)/0.12)] text-[hsl(var(--fc-warning))]' : 'bg-accent text-accent-foreground'}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        {loading ? (
          <Skeleton className="mt-2 h-9 w-24" />
        ) : (
          <div className="mt-2 font-display text-2xl md:text-3xl font-semibold tabular-nums">{value}</div>
        )}
        {!loading && delta !== null && delta !== undefined && (
          <div className="mt-1 flex items-center gap-1 text-xs">
            <DeltaIcon className={`h-3 w-3 ${isUp ? 'text-[hsl(var(--fc-success))]' : 'text-[hsl(var(--fc-warning))]'}`} />
            <span className={`font-medium tabular-nums ${isUp ? 'text-[hsl(var(--fc-success))]' : 'text-[hsl(var(--fc-warning))]'}`}>
              {isUp ? '+' : ''}{delta}%
            </span>
            <span className="text-muted-foreground">{deltaLabel}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AlertRow({ icon: Icon, label, count, to }) {
  return (
    <Link to={to} className="flex items-center justify-between p-2 rounded-md hover:bg-muted/40">
      <div className="flex items-center gap-2 text-sm">
        <Icon className="h-4 w-4 text-muted-foreground" />
        {label}
      </div>
      <Badge variant={count > 0 ? 'default' : 'secondary'} className="tabular-nums">{count}</Badge>
    </Link>
  );
}
